// The control continues to use HeaderDrawer unchanged.
customElements.whenDefined('header-drawer').then(() => {
  if (customElements.get('header-drawer-v2')) return;

  class HeaderDrawerV2 extends customElements.get('header-drawer') {
    bindEvents() {
      this.querySelectorAll('summary').forEach((summary) => {
        summary.addEventListener('click', this.onSummaryClick.bind(this));
      });
      this.querySelectorAll('.menu-drawer__close-button').forEach((button) => {
        button.addEventListener('click', this.onCloseButtonClick.bind(this));
      });
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-perks-open]')?.addEventListener('click', (event) => {
        this.perksOpener = event.currentTarget;
        removeTrapFocus();
        this.dialog.showModal();
      });
      this.querySelector('[data-perks-close]')?.addEventListener('click', () => this.closePerks());
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.perksEscaped = true;
        this.closePerks();
      });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.closePerks();
      });
      this.dialog?.addEventListener('close', () => {
        this.perksAnimation?.cancel();
        this.perksAnimation = null;
        this.dialog.classList.remove('is-closing');
        if (this.mainDetailsToggle.open && this.getClientRects().length) {
          trapFocus(this.mainDetailsToggle, this.perksOpener);
        }
      });
      // Keep the theme's generic details Escape handler out of this drawer.
      this.addEventListener('keyup', (event) => {
        if (event.code !== 'Escape') return;
        event.stopImmediatePropagation();
        if (this.perksEscaped) {
          this.perksEscaped = false;
          return;
        }
        if (!this.dialog?.open && !this.dialog?.contains(event.target)) this.onKeyUp(event);
      }, true);
    }

    closePerks(immediate = false) {
      if (!this.dialog?.open) return;
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.perksAnimation?.cancel();
        this.perksAnimation = null;
        this.dialog.close();
        return;
      }
      if (this.perksAnimation) return;
      const transform = getComputedStyle(this.dialog).transform;
      this.dialog.classList.add('is-closing');
      this.perksAnimation = this.dialog.animate(
        [{ transform }, { transform: 'translateY(100%)' }],
        { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' },
      );
      this.perksAnimation.onfinish = () => this.dialog.close();
    }

    setGoalExpanded(details, expanded, animate = true) {
      const summary = details.querySelector('summary');
      const content = summary.nextElementSibling;
      const startHeight = details.getBoundingClientRect().height;
      const startOpacity = details.open ? getComputedStyle(content).opacity : '0';
      this.goalAnimation?.cancel();
      this.goalFade?.cancel();
      this.goalAnimation = this.goalFade = null;
      details.removeAttribute('data-animating');
      details.open = expanded;
      this.goalExpanded = expanded;
      summary.setAttribute('aria-expanded', String(expanded));
      content.inert = !expanded;
      if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const endHeight = details.getBoundingClientRect().height;
      details.open = true;
      details.setAttribute('data-animating', '');
      const options = { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
      this.goalAnimation = details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }], options,
      );
      this.goalFade = content.animate(
        [{ opacity: startOpacity }, { opacity: expanded ? 1 : 0 }], options,
      );
      this.goalAnimation.onfinish = () => {
        details.open = expanded;
        details.removeAttribute('data-animating');
        this.goalAnimation = this.goalFade = null;
      };
    }

    onSummaryClick(event) {
      const details = event.currentTarget.parentElement;
      if (details.hasAttribute('data-goal')) {
        event.preventDefault();
        this.setGoalExpanded(details, this.goalAnimation ? !this.goalExpanded : !details.open);
        return;
      }
      super.onSummaryClick(event);
    }

    closeSubmenu(details) {
      if (!details.hasAttribute('data-goal')) return super.closeSubmenu(details);
      this.setGoalExpanded(details, false);
      const summary = details.querySelector('summary');
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }

    closeMenuDrawer(event, elementToFocus) {
      // Match HeaderDrawer's guard before changing the dialog on focusout.
      if (!elementToFocus) return;
      this.closePerks(true);
      const goal = this.querySelector('[data-goal]');
      if (goal) this.setGoalExpanded(goal, false, false);
      super.closeMenuDrawer(event, elementToFocus);
      this.querySelector('[data-goal] > summary')?.setAttribute('aria-expanded', 'false');
    }

    connectedCallback() {
      // Also reset an open drawer when resizing out of the experiment.
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.handleViewportChange = () => {
        if (!this.mobileQuery.matches && this.mainDetailsToggle.open) {
          this.closeMenuDrawer(new Event('resize'), this.mainDetailsToggle.querySelector('summary'));
        }
      };
      this.mobileQuery.addEventListener('change', this.handleViewportChange);
    }

    disconnectedCallback() {
      this.perksAnimation?.cancel();
      this.goalAnimation?.cancel();
      this.goalFade?.cancel();
      this.mobileQuery?.removeEventListener('change', this.handleViewportChange);
      window.removeEventListener('resize', this.onResize);
    }
  }

  customElements.define('header-drawer-v2', HeaderDrawerV2);
});
